import { LEGAL_EFFECTIVE_DATE, LEGAL_VERSION } from '../constants/legalContent';
import './LegalDoc.css';

// A deliberately small markdown renderer covering exactly what legal/*.md
// uses: ## headings, **bold**, - bullets, | tables |, --- rules and
// paragraphs. Pulling in a markdown library for three static documents would
// add a dependency (and its transitive tree) to render text that never changes
// shape. If the documents start using something not handled here it will show
// up as literal syntax on the page rather than failing silently.

function inline(text, keyPrefix) {
  // **bold** only - the documents use no italics, links or inline code that
  // matters to their meaning.
  const out = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<strong key={`${keyPrefix}-b${i++}`}>{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Table({ rows, k }) {
  // First row is the header, second is the |---| separator, rest are body.
  const cells = (line) =>
    line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const header = cells(rows[0]);
  const body = rows.slice(2).map(cells);
  return (
    <div className="legal-table-wrap">
      <table className="legal-table">
        <thead>
          <tr>{header.map((c, i) => <th key={i}>{inline(c, `${k}h${i}`)}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri}>{r.map((c, ci) => <td key={ci}>{inline(c, `${k}c${ri}${ci}`)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function render(markdown) {
  const lines = markdown.split('\n');
  const nodes = [];
  let i = 0;
  let para = [];
  let bullets = [];

  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(' ');
    nodes.push(<p key={`p${nodes.length}`}>{inline(text, `p${nodes.length}`)}</p>);
    para = [];
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    nodes.push(
      <ul key={`u${nodes.length}`}>
        {bullets.map((b, bi) => <li key={bi}>{inline(b, `u${nodes.length}i${bi}`)}</li>)}
      </ul>
    );
    bullets = [];
  };
  const flushAll = () => { flushPara(); flushBullets(); };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { flushAll(); i += 1; continue; }

    if (trimmed.startsWith('|')) {
      flushAll();
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(lines[i].trim());
        i += 1;
      }
      if (rows.length >= 2) nodes.push(<Table key={`t${nodes.length}`} rows={rows} k={`t${nodes.length}`} />);
      continue;
    }

    if (trimmed === '---') { flushAll(); nodes.push(<hr key={`h${nodes.length}`} />); i += 1; continue; }

    if (trimmed.startsWith('### ')) {
      flushAll();
      nodes.push(<h3 key={`h${nodes.length}`}>{inline(trimmed.slice(4), `h${nodes.length}`)}</h3>);
      i += 1; continue;
    }
    if (trimmed.startsWith('## ')) {
      flushAll();
      nodes.push(<h2 key={`h${nodes.length}`}>{inline(trimmed.slice(3), `h${nodes.length}`)}</h2>);
      i += 1; continue;
    }
    if (trimmed.startsWith('# ')) {
      flushAll();
      nodes.push(<h1 key={`h${nodes.length}`}>{inline(trimmed.slice(2), `h${nodes.length}`)}</h1>);
      i += 1; continue;
    }

    if (trimmed.startsWith('- ')) {
      flushPara();
      // A bullet can wrap onto following indented lines.
      let item = trimmed.slice(2);
      i += 1;
      while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !lines[i].trim().startsWith('- ')) {
        item += ' ' + lines[i].trim();
        i += 1;
      }
      bullets.push(item);
      continue;
    }

    flushBullets();
    para.push(trimmed);
    i += 1;
  }
  flushAll();
  return nodes;
}

function LegalDoc({ doc, showMeta = true }) {
  if (!doc) return <p>Document not found.</p>;
  return (
    <article className="legal-doc">
      <h1 className="legal-doc-title">{doc.title}</h1>
      {showMeta && (
        <p className="legal-doc-meta">
          Effective {LEGAL_EFFECTIVE_DATE} · Version {LEGAL_VERSION}
        </p>
      )}
      {render(doc.body)}
    </article>
  );
}

export default LegalDoc;
